import React, { useEffect, useState } from "react";
import { BotonVolver } from "@/components/BotonVolver";
import { useSearchParams } from "react-router-dom";
import { Ticket, Plus, MapPin, Users, FileText, Trash2, UserRound, Navigation } from "lucide-react";
import { useData } from "@/context/DataContext";
import { usePlanes, recargarPlanes, fechaPlan, tipoOcio, enlaceMapa, viajesDeHoy, sugerenciaViaje } from "@/lib/ocio";
import { ResumenViaje } from "@/components/ocio/ResumenViaje";
import { SugerenciaViaje } from "@/components/ocio/SugerenciaViaje";
import { abrirVisor } from "@/lib/visor";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { OcioAdd } from "@/components/ocio/OcioAdd";
import { BorrarPlan } from "@/components/ocio/BorrarPlan";
import { PlanHoy } from "@/components/ocio/ProximoPlan";
import { Embarque } from "@/components/ocio/Embarque";
import { CambiarPersona } from "@/components/ocio/CambiarPersona";
import { nombrePersona, SIN_ASIGNAR } from "@/components/ocio/PersonaPlan";

const TABS = [["proximos", "Próximos planes"], ["entradas", "Entradas y reservas"], ["historial", "Historial"]];

const PersonaLinea = ({ p }) => {
  const { familyMembers = [] } = useData();
  const otros = (p.participantes || []).map((id) => nombrePersona(familyMembers, id)).filter((n) => n !== SIN_ASIGNAR);
  return (
    <p className="text-base font-semibold text-[#1C2421] flex items-center gap-1.5 mt-1" data-testid={`ocio-plan-persona-${p.id}`}>
      <UserRound size={16} className="text-[#3A6B56]" /> {nombrePersona(familyMembers, p.family_member_id)}{otros.length ? <span className="font-normal text-[#4A5550]"> · con {otros.join(", ")}</span> : null}
    </p>
  );
};

export const PlanCard = ({ p, onBorrar, onPersona, destacado }) => {
  const { documents = [] } = useData();
  const sustituye = !!p.documento_id && documents.some((d) => d.sustituida && d.sustituida_por === p.documento_id);
  const { planes } = usePlanes();
  const companeros = p.viaje_id ? planes.filter((x) => x.viaje_id === p.viaje_id && x.id !== p.id) : [];
  const separar = async () => {
    try { await api.delete(`/ocio/planes/${p.id}/viaje`); await recargarPlanes(); toast.success("Plan separado del viaje"); }
    catch { toast.error("No se pudo separar."); }
  };
  return (
  <div id={`plan-${p.id}`} className={`bg-white border rounded-2xl p-4 ${destacado ? "border-[#3A6B56] ring-2 ring-[#3A6B56]/40" : "border-[#E5DFD5]"}`} data-testid={`ocio-plan-${p.id}`}>
    <p className="text-sm font-semibold text-[#3A6B56]">{tipoOcio(p.tipo)}</p>
    <p className="font-heading text-lg font-bold text-[#1C2421] leading-tight">{p.nombre}</p>
    <PersonaLinea p={p} />
    <p className="text-base text-[#4A5550] mt-1 first-letter:uppercase">{fechaPlan(p)}</p>
    <PlanHoy p={p} />
    <Embarque p={p} />
    {companeros.length > 0 && (
      <p className="text-sm text-[#3A6B56] mt-1" data-testid={`ocio-plan-viaje-${p.id}`}>
        Mismo viaje que {companeros.map((x) => `«${x.nombre}»`).join(", ")} · <button data-testid={`ocio-plan-separar-${p.id}`} onClick={separar} className="underline underline-offset-4 text-[#4A5550]">Separar</button>
      </p>
    )}
    {p.lugar && <p className="text-sm text-[#6E7A74] flex items-center gap-1 mt-1"><MapPin size={14} /> {p.lugar}</p>}
    {(p.personas || p.codigo) && <p className="text-sm text-[#6E7A74] flex items-center gap-1 mt-1"><Users size={14} /> {[p.personas && `${p.personas} ${["restaurante", "hotel", "viaje"].includes(p.tipo) ? (p.personas === 1 ? "persona" : "personas") : (p.personas === 1 ? "entrada" : "entradas")}`, p.codigo && `Código ${p.codigo}`].filter(Boolean).join(" · ")}</p>}
    <div className="flex flex-wrap gap-2 mt-3">
      {p.documento_id && <button data-testid={`ocio-abrir-entrada-${p.id}`} onClick={() => abrirVisor({ id: p.documento_id, title: p.nombre })}
        className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-xl bg-[#3A6B56] text-white font-semibold"><FileText size={18} /> {sustituye ? "Ver tarjeta vigente" : "Ver entrada"}</button>}
      {sustituye && <span data-testid={`ocio-tarjeta-vigente-${p.id}`} className="self-center px-3 py-1 rounded-full bg-[#E8F0EC] text-[#3A6B56] text-sm font-bold">Vigente · hay una anterior sustituida</span>}
      {enlaceMapa(p) && <a data-testid={`ocio-como-llegar-${p.id}`} href={enlaceMapa(p)} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-xl border-2 border-[#3A6B56] text-[#3A6B56] font-semibold"><Navigation size={18} /> Cómo llegar</a>}
      {onPersona && <button data-testid={`ocio-cambiar-persona-${p.id}`} onClick={() => onPersona(p)} className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-xl border border-[#E5DFD5] text-[#4A5550] font-semibold"><UserRound size={18} /> Cambiar persona</button>}
      {onBorrar && <button data-testid={`ocio-borrar-${p.id}`} onClick={() => onBorrar(p)} className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-xl border border-[#E5DFD5] text-[#4A5550] font-semibold"><Trash2 size={18} /> Borrar plan</button>}
    </div>
  </div>
);
};

const EntradaSuelta = ({ d }) => (
  <div className={`border rounded-2xl p-4 ${d.sustituida ? "bg-[#F6F3EE] border-[#E5DFD5] opacity-80" : "bg-white border-[#E5DFD5]"}`} data-testid={`ocio-entrada-${d.id}`}>
    {d.sustituida ? (
      <p className="text-sm text-[#6E7A74]" data-testid={`ocio-entrada-sustituida-${d.id}`}>
        <span className="inline-block px-2.5 py-0.5 rounded-full bg-[#E5DFD5] text-[#4A5550] font-bold mr-1">Sustituida</span>
        {d.sustituida_plan ? `Tarjeta anterior de «${d.sustituida_plan}». La vigente está en el plan.` : "Hay una tarjeta más reciente."}
      </p>
    ) : <p className="text-sm font-semibold text-[#3A6B56]">Entrada o reserva guardada</p>}
    <p className="font-heading text-lg font-bold text-[#1C2421] leading-tight break-words">{d.filename || d.title || "Documento"}</p>
    <button data-testid={`ocio-abrir-doc-${d.id}`} onClick={() => abrirVisor({ id: d.id, title: d.filename })}
      className="mt-3 inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-xl bg-[#3A6B56] text-white font-semibold"><FileText size={18} /> Ver documento</button>
  </div>
);

export default function Ocio() {
  const [sp, setSp] = useSearchParams();
  const { documents = [], familyMembers = [] } = useData();
  const { planes, proximos, historial } = usePlanes();
  const [tab, setTab] = useState("proximos");
  const [aBorrar, setABorrar] = useState(null);
  const [aPersona, setAPersona] = useState(null);
  const [quien, setQuien] = useState("todos");
  const nuevo = sp.get("nuevo") === "1";
  const planId = sp.get("plan");
  useEffect(() => {
    if (!planId) return;
    if (historial.some((p) => p.id === planId)) setTab("historial"); else if (proximos.some((p) => p.id === planId)) setTab("proximos");
    setQuien("todos");
    setTimeout(() => document.getElementById(`plan-${planId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
  }, [planId, planes.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const dePersona = (id) => quien === "todos" || (quien === "sin" ? !id : id === quien);
  const conPlan = new Set(planes.map((p) => p.documento_id).filter(Boolean));
  const sueltas = documents.filter((d) => (d.origen === "ocio" || d.category === "Ocio") && !d.is_deleted && !conPlan.has(d.id) && dePersona(d.family_member_id));
  const viajes = tab === "proximos" && quien === "todos" ? viajesDeHoy(planes) : [];
  const enViaje = new Set(viajes.flat().map((p) => p.id));
  const sugerencia = tab === "proximos" && quien === "todos" ? sugerenciaViaje(proximos) : null;
  const lista = (tab === "proximos" ? proximos.filter((p) => !enViaje.has(p.id)) : tab === "historial" ? historial : planes.filter((p) => p.documento_id))
    .filter((p) => dePersona(p.family_member_id) || (quien !== "todos" && quien !== "sin" && (p.participantes || []).includes(quien)));
  const opciones = [["todos", "Todos"], ...familyMembers.filter((m) => planes.some((p) => p.family_member_id === m.id || (p.participantes || []).includes(m.id))).map((m) => [m.id, m.name]),
    ...(planes.some((p) => !p.family_member_id) ? [["sin", SIN_ASIGNAR]] : [])];
  const borrar = setABorrar;
  return (
    <div className="space-y-5" data-testid="ocio-page">
      <BotonVolver porDefecto="/areas" testid="ocio-volver" />
      <div className="flex items-center gap-3">
        <span className="w-12 h-12 rounded-2xl bg-[#E8F0EC] flex items-center justify-center"><Ticket size={26} className="text-[#3A6B56]" /></span>
        <h1 className="font-heading text-3xl font-bold text-[#1C2421]">Ocio y Reservas</h1>
      </div>
      <button data-testid="ocio-nuevo" onClick={() => setSp({ nuevo: "1" })} className="w-full flex items-center justify-center gap-2 min-h-[56px] rounded-2xl bg-[#3A6B56] text-white font-semibold text-lg"><Plus size={22} /> Añadir entrada o reserva</button>
      <div className="flex gap-2 overflow-x-auto" role="tablist">
        {TABS.map(([id, l]) => (
          <button key={id} role="tab" data-testid={`ocio-tab-${id}`} onClick={() => setTab(id)}
            className={`shrink-0 min-h-[44px] px-4 rounded-xl font-semibold border ${tab === id ? "bg-[#1C2421] text-white border-[#1C2421]" : "bg-white text-[#4A5550] border-[#E5DFD5]"}`}>{l}</button>
        ))}
      </div>
      {opciones.length > 2 && (
        <div className="flex flex-wrap gap-2" data-testid="ocio-filtro-persona">
          {opciones.map(([id, l]) => (
            <button key={id} data-testid={`ocio-filtro-${id}`} aria-pressed={quien === id} onClick={() => setQuien(id)}
              className={`shrink-0 min-h-[44px] px-4 rounded-full font-semibold border-2 ${quien === id ? "bg-[#3A6B56] border-[#3A6B56] text-white" : "bg-white text-[#1C2421] border-[#E5DFD5]"}`}>{l}</button>
          ))}
        </div>
      )}
      <div className="space-y-3" data-testid={`ocio-lista-${tab}`}>
        {tab === "entradas" && sueltas.filter((d) => !d.sustituida).map((d) => <EntradaSuelta key={d.id} d={d} />)}
        {sugerencia && <SugerenciaViaje s={sugerencia} />}
        {viajes.map((g) => (
          <ResumenViaje key={g[0].viaje_id} planes={g}>
            {g.filter((p) => proximos.some((x) => x.id === p.id)).map((p) => <PlanCard key={p.id} p={p} destacado={p.id === planId} onPersona={setAPersona} onBorrar={borrar} />)}
          </ResumenViaje>
        ))}
        {lista.length || viajes.length || (tab === "entradas" && sueltas.length) ? lista.map((p) => <PlanCard key={p.id} p={p} destacado={p.id === planId} onPersona={setAPersona} onBorrar={tab === "historial" ? null : borrar} />)
          : <p className="text-base text-[#6E7A74] bg-white border border-dashed border-[#E5DFD5] rounded-2xl p-5" data-testid="ocio-vacio">{tab === "historial" ? "Aquí aparecerán tus planes cuando hayan pasado." : "Todavía no hay nada aquí. Añade una entrada o reserva."}</p>}
        {tab === "entradas" && sueltas.filter((d) => d.sustituida).map((d) => <EntradaSuelta key={d.id} d={d} />)}
      </div>
      {nuevo && <OcioAdd onClose={() => setSp({})} />}
      {aPersona && <CambiarPersona plan={aPersona} onClose={() => setAPersona(null)} />}
      {aBorrar && <BorrarPlan plan={aBorrar} onClose={() => setABorrar(null)} />}
    </div>
  );
}
